import { getDailyNumber } from "@/lib/karma/daily";
import { getLocalDateKey } from "@/lib/karma/missions";
import { COACH_CORE_MISSION_IDS } from "@/lib/karma/missions";
import type { KarmaCoachEvidence, KarmaCoachState, KarmaUser } from "@/lib/karma/types";

const ALL_MISSION_IDS = [
  "core-starter",
  "core-main",
  "core-closing",
  "bonus-affirmation",
  "bonus-micro",
] as const;

export type KarmaCoachTitle = {
  id: string;
  name: string;
  description: string;
};

function parseLocalDateKey(dateKey: string) {
  const [y, m, d] = dateKey.split("-").map((x) => Number(x));
  const year = Number.isFinite(y) ? y : 2000;
  const month = Number.isFinite(m) ? m : 1;
  const day = Number.isFinite(d) ? d : 1;
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

function unique<T>(arr: T[]) {
  return Array.from(new Set(arr));
}

function listAllDateKeys(coach: KarmaCoachState | undefined) {
  if (!coach) return [];
  const keys = [
    ...Object.keys(coach.completedByDate ?? {}),
    ...Object.keys(coach.dismissedByDate ?? {}),
    ...Object.keys(coach.skippedByDate ?? {}),
    ...Object.keys(coach.evidenceByDate ?? {}),
    ...Object.keys(coach.notesByDate ?? {}),
    ...Object.keys(coach.submittedByDate ?? {}),
    ...Object.keys(coach.verifiedByDate ?? {}),
  ];
  return unique(keys).sort();
}

function isCompleted(coach: KarmaCoachState | undefined, dateKey: string, missionId: string) {
  return (coach?.completedByDate?.[dateKey] ?? []).includes(missionId);
}

function isSkipped(coach: KarmaCoachState | undefined, dateKey: string, missionId: string) {
  return (coach?.skippedByDate?.[dateKey] ?? []).includes(missionId);
}

function isVerified(coach: KarmaCoachState | undefined, dateKey: string, missionId: string) {
  return (coach?.verifiedByDate?.[dateKey] ?? []).includes(missionId);
}

function getEvidence(coach: KarmaCoachState | undefined, dateKey: string, missionId: string) {
  return (coach?.evidenceByDate?.[dateKey]?.[missionId] ?? {}) as KarmaCoachEvidence;
}

function getClosingAnswer(coach: KarmaCoachState | undefined, dateKey: string) {
  return coach?.notesByDate?.[dateKey]?.closingAnswer ?? "";
}

function hasTimerDone(evidence: KarmaCoachEvidence) {
  return (evidence.checks ?? []).includes("__timer_done");
}

function mainQualityXp(evidence: KarmaCoachEvidence) {
  const answer = (evidence.checks ?? [])[0] ?? "";
  if (answer === "Yaptım") return 25;
  if (answer === "Kısmen yaptım") return 15;
  if (answer === "Erteledim") return 5;
  return 8;
}

function closingQualityXp(text: string) {
  const len = text.trim().length;
  if (len >= 40) return 25;
  if (len >= 10) return 20;
  if (len >= 1) return 10;
  return 5;
}

function applySkipPenalty(xp: number, skipped: boolean) {
  if (!skipped) return xp;
  return Math.max(0, Math.round(xp * 0.3));
}

export function computeCoachXpForDate(user: KarmaUser, dateKey: string) {
  const coach = user.coach;

  let total = 0;

  for (const missionId of ALL_MISSION_IDS) {
    if (!isCompleted(coach, dateKey, missionId)) continue;

    const evidence = getEvidence(coach, dateKey, missionId);
    const skipped = isSkipped(coach, dateKey, missionId);

    let xp = 0;

    if (missionId === "core-starter") {
      xp = hasTimerDone(evidence) ? 10 : 3;
    } else if (missionId === "core-main") {
      xp = mainQualityXp(evidence);
    } else if (missionId === "core-closing") {
      xp = closingQualityXp(getClosingAnswer(coach, dateKey));
    } else if (missionId === "bonus-affirmation") {
      xp = hasTimerDone(evidence) ? 8 : 3;
    } else if (missionId === "bonus-micro") {
      xp = 6;
    }

    let earned = applySkipPenalty(xp, skipped);

    if (missionId === "core-main" || missionId === "core-closing") {
      if (isVerified(coach, dateKey, missionId)) {
        earned += 10;
      }
    }

    total += earned;
  }

  return total;
}

export function isCoachCoreCompleteForDate(user: KarmaUser, dateKey: string) {
  const ids = user.coach?.completedByDate?.[dateKey] ?? [];
  return COACH_CORE_MISSION_IDS.every((id) => ids.includes(id));
}

export function computeCoachStreak(user: KarmaUser, now: Date = new Date()) {
  let streak = 0;
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0, 0);

  for (let i = 0; i < 365; i += 1) {
    const key = getLocalDateKey(d);
    if (!isCoachCoreCompleteForDate(user, key)) break;
    streak += 1;
    d.setDate(d.getDate() - 1);
  }

  return streak;
}

export function computeCoachTotalXp(user: KarmaUser) {
  const keys = listAllDateKeys(user.coach);
  return keys.reduce((acc, k) => acc + computeCoachXpForDate(user, k), 0);
}

export function computeCoachLevel(totalXp: number) {
  const level = Math.max(1, Math.floor(totalXp / 100) + 1);
  const intoLevel = totalXp % 100;
  const nextAt = level * 100;
  return {
    level,
    intoLevel,
    nextAt,
    progress: Math.round((intoLevel / 100) * 100),
  };
}

function countThemeDays(user: KarmaUser, dailyNumber: number) {
  const keys = listAllDateKeys(user.coach);
  let count = 0;

  for (const key of keys) {
    if (!isCoachCoreCompleteForDate(user, key)) continue;
    const date = parseLocalDateKey(key);
    if (getDailyNumber(date) !== dailyNumber) continue;

    const evidence = getEvidence(user.coach, key, "core-main");
    const quality = (evidence.checks ?? [])[0] ?? "";
    if (quality !== "Yaptım") continue;

    if (!isVerified(user.coach, key, "core-main")) continue;

    count += 1;
  }

  return count;
}

export function computeCoachTitles(user: KarmaUser): KarmaCoachTitle[] {
  const titles: KarmaCoachTitle[] = [];

  const streak = computeCoachStreak(user);
  if (streak >= 21) {
    titles.push({
      id: "istikrar-ustasi",
      name: "İstikrar Ustası",
      description: "21 gün üst üste 3 ana görevi tamamladı.",
    });
  }

  const duzen = countThemeDays(user, 4);
  if (duzen >= 7) {
    titles.push({
      id: "duzen-kurucu",
      name: "Düzen Kurucu",
      description: "Enerji 4 günlerinde 7 kez onaylı (verified) ana eylem yaptı.",
    });
  }

  const tamam = countThemeDays(user, 9);
  if (tamam >= 7) {
    titles.push({
      id: "tamamlayici",
      name: "Tamamlayıcı",
      description: "Enerji 9 günlerinde 7 kez onaylı (verified) ana eylem yaptı.",
    });
  }

  const ifadeKeys = listAllDateKeys(user.coach);
  let ifadeCount = 0;
  for (const key of ifadeKeys) {
    if (!isCoachCoreCompleteForDate(user, key)) continue;
    const date = parseLocalDateKey(key);
    if (getDailyNumber(date) !== 3) continue;
    if (getClosingAnswer(user.coach, key).trim().length < 10) continue;
    if (!isVerified(user.coach, key, "core-closing")) continue;
    ifadeCount += 1;
  }

  if (ifadeCount >= 7) {
    titles.push({
      id: "ifade-acici",
      name: "İfade Açıcı",
      description: "Enerji 3 günlerinde 7 kez onaylı (verified) kapanış notu bıraktı.",
    });
  }

  return titles;
}

export function computeCoachTodaySummary(user: KarmaUser, now: Date = new Date()) {
  const todayKey = getLocalDateKey(now);
  const todayXp = computeCoachXpForDate(user, todayKey);
  const totalXp = computeCoachTotalXp(user);
  const level = computeCoachLevel(totalXp);
  const streak = computeCoachStreak(user, now);
  const titles = computeCoachTitles(user);

  return {
    todayKey,
    todayXp,
    totalXp,
    level,
    streak,
    titles,
  };
}
